/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jun 19 2023
 **/

import { useEffect, useState } from "react";
import { timer } from "rxjs";

import { Button, Menu, useModal, useToast } from "@/frameworks/designStuff/components";
import { GROUP_POPOVER_ID, ModalID, ZIndex } from "@/frameworks/core/commons";
import { ChatAlarmSetting, ShowChatMember } from "@/frameworks/core/components";
import { useFullSizeChatManager, useMiniChatPopupManager, usePopover } from "@/frameworks/core/hooks";
import { useChatRoomActions, useDMRoomState } from "@/frameworks/core/chat";
import { useVoiceCall } from "@/frameworks/core/API";
import { usePersistUser } from "@/frameworks/core/persistence";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { EventHandler } from "@rfice/eventhandler";

import * as S from "./emotion";

export default function DMChatOptionList({ channelID }: { channelID: string }) {
	const { useFullSizeChatState } = useFullSizeChatManager();
	const fullSizeChatState = useFullSizeChatState();
	const me = usePersistUser("me");
	const [isJoined, setIsJoined] = useState(false);
	const modal = useModal();
	const popover = usePopover();
	const { fullSizeChatActions } = useFullSizeChatManager();

	const chatRoomActions = useChatRoomActions();
	const { miniChatPopupActions } = useMiniChatPopupManager();

	const dmRoom = useDMRoomState(channelID);
	const toast = useToast();
	const voiceCallAPI = useVoiceCall({
		onSuccess(name) {
			switch (name) {
				case "voicecall:checkJoined": {
					//내가 참여한 보이스챗이 하나라도 있는지 여부
					const { data } = voiceCallAPI.checkJoined.result;
					if (data) {
						setIsJoined(data.joined);
					}
					break;
				}
			}
		}
	});
	useEffect(() => {
		voiceCallAPI.checkJoined.execute();
	}, []);

	const handleClick = async (evt: React.MouseEvent<HTMLElement>) => {
		evt.stopPropagation();
		if (!dmRoom) return;
		const target = evt.currentTarget as HTMLElement;
		const option = target.dataset["option"] as
			| "pin"
			| "show-chat-member"
			| "voicecall"
			| "remove-message"
			| "alarm";

		switch (option) {
			case "pin": {
				chatRoomActions.common.pin({ roomID: channelID, pin: !dmRoom.pinned });
				break;
			}
			case "show-chat-member": {
				modal.show({
					id: ModalID.SHOW_CHAT_MEMBER,
					content: (
						<ShowChatMember
							onHide={() => modal.hide(ModalID.SHOW_CHAT_MEMBER)}
							channelID={channelID}
							chatKind="DM"
							me={me.username}
						/>
					)
				});
				break;
			}
			case "voicecall": {
				if (isJoined) {
					toast.show({
						type: "system",
						message: _T("이미 진행중인 보이스챗이 있습니다. 종료하고 진행해주세요.")
					});
				} else {
					await chatRoomActions.common.open({ type: "DM", channelID: channelID });
					timer(10).subscribe(() => EventHandler.fire("$VoiceCall::Starting$", channelID));
				}
				popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
				break;
			}
			case "remove-message": {
				try {
					await chatRoomActions.dm.hide({ channelID });
					if (channelID === fullSizeChatState?.channelID) {
						fullSizeChatActions.hide();
					}
					miniChatPopupActions.hide(channelID);
				} catch {
					throw "대화목록 삭제 실패";
				}
				break;
			}
			case "alarm": {
				popover.toggle({
					popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
					uniqueID: "alarm",
					content: (
						<ChatAlarmSetting
							isGeneral={false}
							roomID={dmRoom.roomId}
							currentOption={dmRoom.notificationType}
							popoverID={GROUP_POPOVER_ID.CHAT_OPTION}
						/>
					),
					anchorEl: target,
					direction: {
						x: "right",
						y: "top"
					},
					position: {
						x: 60,
						y: 0
					},
					zIndex: ZIndex.POPUP + 1
				});
				return;
			}
		}
		popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
	};

	if (!dmRoom) return;
	return (
		<S.SubMenu>
			<Menu.List>
				<Menu.Item onClick={handleClick} data-option="pin" typoSize="Body3_MD">
					<Maybe test={dmRoom.pinned} fallback={_T("고정")}>
						{_T("고정 해제")}
					</Maybe>
				</Menu.Item>
				{dmRoom.users.length !== 1 && (
					<S.AlarmSettingMenu
						type="other"
						onClick={handleClick}
						notificationType={dmRoom.notificationType}
						data-option="alarm"
					/>
				)}
				<Maybe
					test={Boolean(
						!(dmRoom.users.length === 1 && dmRoom.users.map((user) => user.id === Configure.userID))
					)}
				>
					<Menu.Item onClick={handleClick} data-option="show-chat-member" typoSize="Body3_MD">
						{_T("채팅멤버 보기 및 추가")}
					</Menu.Item>
				</Maybe>
				<Maybe
					test={Boolean(
						!(dmRoom.users.length === 1 && dmRoom.users.map((user) => user.id === Configure.userID))
					)}
				>
					{dmRoom.inVoiceChat ? (
						<Button
							disabled={true}
							filled="neutral"
							style={{ width: "100%", justifyContent: "flex-start", padding: "6px 8px" }}
						>
							{_T("보이스챗")}
						</Button>
					) : (
						<Menu.Item onClick={handleClick} data-option="voicecall" typoSize="Body3_MD">
							{_T("보이스챗")}
						</Menu.Item>
					)}
				</Maybe>
				<Menu.Item onClick={handleClick} data-option="remove-message" typoSize="Body3_MD">
					{_T("대화 목록 삭제")}
				</Menu.Item>
			</Menu.List>
		</S.SubMenu>
	);
}
